/**
 * Call Session Action Functions - Fetch operations for Call Sessions
 *
 * These functions handle all call session fetch operations.
 * Uses validated fetch with Zod schemas for runtime type safety.
 */

import { CallSessionSchema, type CallSession } from "@/isomorphic/schemas";
import { getAtomStore } from "@lib/atoms";
import { API_ROUTES } from "@/frontend/lib/api/routes";
import { fetchValidated, fetchValidatedArray } from "@/frontend/lib/validation";
import {
  _apiFollowUpCallSessionsAtom,
  followUpCallSessionsStatusAtom,
  followUpCallSessionsErrorAtom,
  currentCallSessionAtom,
  currentCallSessionStatusAtom,
} from "../core/call-sessions";

// ============================================================================
// FETCH FUNCTIONS
// ============================================================================

/**
 * Fetch all call sessions for a follow-up
 */
export const fetchFollowUpCallSessions = async (jwt: string, followUpId: string): Promise<void> => {
  const store = getAtomStore();

  if (!jwt || !followUpId) {
    return;
  }

  store.set(followUpCallSessionsStatusAtom, "loading");
  store.set(followUpCallSessionsErrorAtom, null);

  const result = await fetchValidatedArray(CallSessionSchema, API_ROUTES.callSessions.getByFollowUp(followUpId), {
    jwt,
    context: `Follow-up ${followUpId} Call Sessions`,
  });

  if (result.success) {
    store.set(_apiFollowUpCallSessionsAtom, result.data);
    store.set(followUpCallSessionsStatusAtom, "success");
  } else {
    console.error("[CallSessionActions] Error fetching follow-up call sessions:", result.error);
    store.set(followUpCallSessionsStatusAtom, "error");
    store.set(followUpCallSessionsErrorAtom, result.error);
  }
};

/**
 * Fetch a single call session by ID
 */
export const fetchCallSession = async (jwt: string, sessionId: string): Promise<CallSession | null> => {
  const store = getAtomStore();

  if (!jwt || !sessionId) {
    return null;
  }

  store.set(currentCallSessionStatusAtom, "loading");

  const result = await fetchValidated(CallSessionSchema, API_ROUTES.callSessions.getById(sessionId), {
    jwt,
    context: `Call Session ${sessionId}`,
  });

  if (result.success) {
    store.set(currentCallSessionAtom, result.data);
    store.set(currentCallSessionStatusAtom, "success");
    return result.data;
  } else {
    console.error("[CallSessionActions] Error fetching call session:", result.error);
    store.set(currentCallSessionStatusAtom, "error");
    return null;
  }
};
